import {
	DefaultExcludedAdjustmentKeys,
	applyPresetStrengthToUserLutAdjustments,
	defaultWhiteBalance,
	getDefaultAdjustments,
	interpolateAdjustments,
} from "@/core/adjustments"
import { AdjustmentKey, Adjustments, Crop, MaskGroup } from "@/core/types"
import {
	PartialAdjustmentsMap,
	getFeatureDistances,
	getOverallLightDistance,
	getTimeWeightDecreasedOverallDistToRef,
	lightKeys,
	refCurrentAdjustmentsId,
	wbKeys,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { extractKeys } from "@/lib/ai/calibration/ai-calibration-utils"
import { ImageInfoRecord, PhotoRecord, StyleInfoRecord } from "@/lib/catalog"
import { fetchPresetIDsFromHistory } from "../store-history"
import { clamp, transformLocalAdjustments } from "@/utils/math"
import { vec2 } from "gl-matrix"
import { constrainCrop } from "@/components/Crop/crop-interaction-handlers"
import { logAiInfo } from "@/lib/ai/utils"
import equal from "fast-deep-equal"
import {
	allPresetsAtom,
	getPresetIdWithStrength,
	loadPresetAdjustments,
	nullPresetID,
	parsePresetIdAndStrength,
} from "../store-presets"
import { AI } from "@/lib/ai/ai"
import { isObjectMask } from "@/core/utils/masks"
import { Sentry } from "@/telemetry/sentry"
import { autoPerspectiveAtom, autoStraightenAtom } from "../settings-state"
import { store } from "../store"
import {
	getAutoLightParams,
	getMaxExposureWithFace,
} from "@/lib/ai/calibration/ai-calibration-brightness"
import {
	getClampedWBBias,
	getLightingLimits,
	InputColorMatchFeaturesWithFaceData,
	logColorMatch,
} from "@/lib/ai/color-match/utils"
import { ColorSpaceCpu } from "@/lib/ai/color-match/renderer-cpu"
import { renderCpu } from "@/lib/ai/color-match/renderer-cpu"
import { createGrayBand } from "@/lib/ai/color-match/lut-utils"
import { getExposure } from "@/lib/ai/color-match/parameters"
import { applyTransform, srgbToProphoto } from "@/lib/ai/color-match/color-space"
import { getAutoWBParamsForColorMatch } from "@/lib/ai/calibration/ai-calibration-wb"

const presetIdSep = "<$&-preset-&$>"

export interface PhotoInfo {
	photo: PhotoRecord
	currentAdjustments: Adjustments
	currentImageInfo: ImageInfoRecord
}

export interface PresetInfo {
	presetId: string
	transformedPresetAdjustments: Partial<Adjustments>
	presetAdjustmentsMap: PartialAdjustmentsMap
	style: StyleInfoRecord | null
}

export function transformLocalAdjustmentsForPaste(
	imageInfo: ImageInfoRecord,
	localAdjustments: MaskGroup[],
	adjustments: Adjustments,
): MaskGroup[] {
	// Get originalImageSize/initialOrientation from imageInfo, or directly from AI editor if we expose some api
	const initialOrientation = imageInfo?.features.initialOrientation ?? 0
	const [w, h] = imageInfo?.features.imageSize ?? [400, 300] // oriented
	const originalImageSize: vec2 = Math.abs(initialOrientation) % 2 ? [h, w] : [w, h]

	return transformLocalAdjustments(
		localAdjustments,
		adjustments,
		originalImageSize,
		initialOrientation,
		false,
	)
}

export function transformPresetMaskForPaste(
	imageInfo: ImageInfoRecord,
	presetAdjustments: Partial<Adjustments>,
	currentAdjustments: Adjustments,
) {
	const transformedAdjustments = { ...presetAdjustments }

	const localAdjustments = presetAdjustments.localAdjustments
	if (localAdjustments) {
		transformedAdjustments["localAdjustments"] = transformLocalAdjustmentsForPaste(
			imageInfo,
			localAdjustments,
			currentAdjustments,
		)
	}

	return transformedAdjustments
}

// Maintain user's non-color adjustments if they exist
export function combineAdjustmentsAndMaintainNonColorParams(
	currentAdjustments: Adjustments,
	adjustments: Adjustments,
) {
	const userAdjustments = extractKeys(currentAdjustments, DefaultExcludedAdjustmentKeys)
	adjustments = { ...adjustments, ...userAdjustments }

	return adjustments
}

export function addAutoStraightenParams(
	adjustments: Partial<Adjustments>,
	currentImageInfo: ImageInfoRecord,
) {
	const straightenAdjustments = extractKeys(currentImageInfo.features.autoCorrection, [
		"crop",
		"cropStraighten",
	])
	return { ...adjustments, ...straightenAdjustments }
}

export function addAutoPerspectiveParams(
	adjustments: Partial<Adjustments>,
	currentImageInfo: ImageInfoRecord,
) {
	const initialCrop: Crop = [0, 0, 1, 1]
	const perspectiveAdjustments = {
		...adjustments,
		...currentImageInfo.features.autoCorrection,
		crop: initialCrop,
	}
	const defaultAdjustments = getDefaultAdjustments()
	const crop = constrainCrop(
		{ ...defaultAdjustments, ...perspectiveAdjustments },
		currentImageInfo.features.imageSize,
		currentImageInfo.features.initialOrientation ?? 0,
	)
	return { ...perspectiveAdjustments, crop }
}

export function addAutoCorrectionParamsIfNeeded(
	adjustments: Partial<Adjustments>,
	currentImageInfo: ImageInfoRecord,
) {
	let aiAdjustments = adjustments
	if (store.get(autoPerspectiveAtom)) {
		// straighten included
		aiAdjustments = addAutoPerspectiveParams(adjustments, currentImageInfo)
	} else if (store.get(autoStraightenAtom)) {
		aiAdjustments = addAutoStraightenParams(adjustments, currentImageInfo)
	}
	return aiAdjustments
}

export async function getIndividualAndMergedPresetAdjustments(
	presetMap: Record<string, Partial<Adjustments>>,
	aggregatedPresetId: string,
	defaultAdjustments: Adjustments,
): Promise<{
	mergedAdjustments: Partial<Adjustments> | undefined
	individualPresetMap: Record<string, Partial<Adjustments>>
}> {
	let mergedAdjustments: Partial<Adjustments> = {}
	const individualPresetMap: Record<string, Partial<Adjustments>> = {}
	const presetIdWithStrengthList = getIndividualPresetIds(aggregatedPresetId)
	let missingPresets = 0
	for (const presetIdWithStrength of presetIdWithStrengthList) {
		const presetAdjustments = await getPresetAdjustments(
			presetMap,
			presetIdWithStrength,
			defaultAdjustments,
		)
		if (!presetAdjustments) {
			missingPresets++
			continue
		}
		mergedAdjustments = { ...mergedAdjustments, ...presetAdjustments }

		const { presetId } = parsePresetIdAndStrength(presetIdWithStrength)
		if (presetId) {
			const presetIdWithStrength1 = getPresetIdWithStrength(presetId, 1)
			const individualPresetAdjustments = await getPresetAdjustments(
				presetMap,
				presetIdWithStrength1,
				defaultAdjustments,
			)
			if (individualPresetAdjustments) {
				individualPresetMap[presetIdWithStrength1] = individualPresetAdjustments
			}
		}
	}
	if (missingPresets === presetIdWithStrengthList.length) {
		return { mergedAdjustments: undefined, individualPresetMap: {} }
	}
	return { mergedAdjustments, individualPresetMap }
}

/**
 * Checks whether an aggregated presetId consists entirely of real presets
 * (system or user), as opposed to derived/synthetic presets created during
 * reference approval. Each segment in the aggregated ID is checked individually.
 */
export function isTruePreset(aggregatedPresetId: string) {
	const segments = getIndividualPresetIds(aggregatedPresetId)
	const allPresets = store.get(allPresetsAtom)

	return segments.every((segment) => {
		const { presetId } = parsePresetIdAndStrength(segment)

		if (allPresets.some((p) => p.id === presetId)) {
			return true
		}

		// Presets from pretrained styles may not be in allPresetsAtom.
		// User-applied presets always carry "&&strength=", derived ones never do.
		if (presetId && segment.includes("&&strength=")) {
			return true
		}

		return false
	})
}

export async function getPresetAdjustments(
	presetMap: Record<string, Partial<Adjustments>>,
	presetIdWithStrength: string,
	defaultAdjustments: Adjustments,
): Promise<Partial<Adjustments> | undefined> {
	logAiInfo("getPresetAdjustments:", { presetIdWithStrength, presetMap })
	let presetAdjustments = presetMap[presetIdWithStrength]
	if (presetAdjustments) return presetAdjustments

	const { presetId, strength } = parsePresetIdAndStrength(presetIdWithStrength)

	if (!presetId) return {}

	presetAdjustments = presetMap[presetId]

	// we look for applied presetAdjustments from style.presets and imageInfo.presetAdjustmentsMap;
	// for whatever reason, we can't find it in either places (like style missing, catalog corrupted etc.),
	// try to get it from allPresetsAtom or return undefined
	if (!presetAdjustments) {
		const allPresets = store.get(allPresetsAtom)
		const preset = allPresets.find((p) => p.id === presetId)
		if (preset) presetAdjustments = await loadPresetAdjustments(preset)
	}
	if (!presetAdjustments) {
		console.warn("Can't find preset adjustments for: " + presetId)
		Sentry.captureMessage("Can't find preset adjustments", {
			level: "warning",
			extra: { presetId },
		})
		return undefined
	}

	if (strength === 1) {
		return applyPresetStrengthToUserLutAdjustments(
			presetAdjustments,
			presetAdjustments,
			strength,
		)
	}

	const interpolatedAdjustments = interpolateAdjustments(
		{ ...defaultAdjustments, ...defaultWhiteBalance },
		presetAdjustments,
		strength,
	)

	return applyPresetStrengthToUserLutAdjustments(
		interpolatedAdjustments,
		presetAdjustments,
		strength,
	)
}

export function getIndividualPresetIds(aggregatedPresetId: string) {
	return aggregatedPresetId.split(presetIdSep)
}

export async function aggregatePresets(input: {
	presetIds: string[] // latest preset first
	style: StyleInfoRecord | null
	presetAdjustmentsMap: PartialAdjustmentsMap
	defaultAdjustments: Adjustments
}) {
	const { presetIds, style, presetAdjustmentsMap, defaultAdjustments } = input
	const stylePresets = style ? style.presets : {}
	//Only merge non-null/undefined values
	const presetMap = {
		...presetAdjustmentsMap,
		...Object.fromEntries(
			Object.entries(stylePresets).filter(([_key, value]) => value != null),
		),
	}
	const uniquePresetIds: string[] = []
	const uniquePresetIdsWithoutStrength: Set<string> = new Set()
	for (const presetIdWithStrength of presetIds) {
		const { presetId } = parsePresetIdAndStrength(presetIdWithStrength)
		if (!presetId) continue
		if (uniquePresetIdsWithoutStrength.has(presetId)) continue
		uniquePresetIdsWithoutStrength.add(presetId)
		uniquePresetIds.push(presetIdWithStrength)
	}

	if (uniquePresetIds.length === 0) {
		return { aggregatedId: null, aggregatedAdjustments: {} }
	}

	if (
		uniquePresetIds.length > 1 &&
		uniquePresetIds[uniquePresetIds.length - 1] === nullPresetID
	) {
		uniquePresetIds.pop()
	}

	if (uniquePresetIds.length === 1) {
		const id = uniquePresetIds[0] as string
		const presetAdjustments = await getPresetAdjustments(presetMap, id, defaultAdjustments)
		if (!presetAdjustments) return { aggregatedId: null, aggregatedAdjustments: {} }
		return {
			aggregatedId: id,
			aggregatedAdjustments: presetAdjustments,
		}
	}

	let finalAdjustments: Partial<Adjustments> = {}
	for (const id of uniquePresetIds) {
		const presetAdjustments = await getPresetAdjustments(presetMap, id, defaultAdjustments)
		if (!presetAdjustments) continue
		presetMap[id] = presetAdjustments
		finalAdjustments = { ...presetAdjustments, ...finalAdjustments }
	}

	const aggregatedPresetIds: string[] = []
	let aggregatedAdjustments: Partial<Adjustments> = {}
	for (let i = 0; i < uniquePresetIds.length; i++) {
		const id = uniquePresetIds[i] as string
		const presetAdjustments = presetMap[id]
		if (!presetAdjustments) continue

		// simple merging scheme: no mask category resolution
		const currAdjustments = { ...presetAdjustments, ...aggregatedAdjustments }
		if (!equal(aggregatedAdjustments, currAdjustments)) {
			aggregatedPresetIds.push(id)
			aggregatedAdjustments = currAdjustments

			if (
				i === uniquePresetIds.length - 1 ||
				equal(finalAdjustments, aggregatedAdjustments)
			) {
				break
			}
		}
	}
	aggregatedPresetIds.reverse()
	const aggregatedId = aggregatedPresetIds.length ? aggregatedPresetIds.join(presetIdSep) : null
	logAiInfo("aggregatePresets", { uniquePresetIds, aggregatedPresetIds })
	return { aggregatedId, aggregatedAdjustments }
}

export async function aggregatePresetsAndCheckOverwriting(
	photoInfo: PhotoInfo,
	presetInfo: PresetInfo,
) {
	const { photo, currentAdjustments, currentImageInfo } = photoInfo
	const { presetId, transformedPresetAdjustments, presetAdjustmentsMap, style } = presetInfo
	const defaultAdjustments = getDefaultAdjustments(photo)

	let aggregatedPresetId = presetId

	const currColorAdjustments = { ...currentAdjustments }
	const keysToRemove: AdjustmentKey[] = [
		...lightKeys,
		...wbKeys,
		...DefaultExcludedAdjustmentKeys,
	]
	if (currColorAdjustments.saturation < 0 && currColorAdjustments.saturation >= -0.24) {
		keysToRemove.push("saturation")
	}
	keysToRemove.forEach((key) => {
		delete currColorAdjustments[key]
	})
	// base for computing auto: previous color adjustments excluding auto (light+WB+sat) + preset(exclude temp)
	const baseAdjustments = {
		...defaultAdjustments,
		...currColorAdjustments,
		...transformedPresetAdjustments,
	}
	baseAdjustments.temperature = undefined

	// adjustments as if the preset is overwriting everything
	const overwritingPresetAdjmt = { ...defaultAdjustments, ...transformedPresetAdjustments }
	overwritingPresetAdjmt.temperature = undefined

	let isOverwriting = equal(baseAdjustments, overwritingPresetAdjmt)

	if (!isOverwriting) {
		const prevPresetIds = await fetchPresetIDsFromHistory(photo) // lastest is at index=0
		const presetIds = [presetId, ...prevPresetIds]
		const res = await aggregatePresets({
			presetIds,
			style,
			presetAdjustmentsMap,
			defaultAdjustments,
		})
		aggregatedPresetId = res.aggregatedId ?? presetId

		if (aggregatedPresetId !== presetId) {
			const aggPresetAdjustments = res.aggregatedAdjustments

			const transformedAggPresetAdjmt = transformPresetMaskForPaste(
				currentImageInfo,
				aggPresetAdjustments,
				currentAdjustments,
			)
			const overwritingAggPresetAdjmt = {
				...defaultAdjustments,
				...transformedAggPresetAdjmt,
			}
			overwritingAggPresetAdjmt.temperature = undefined
			baseAdjustments.tint = aggPresetAdjustments.tint

			isOverwriting = equal(baseAdjustments, overwritingAggPresetAdjmt)
			logAiInfo("ApplyPreset isOverwriting compare:", {
				baseAdjustments,
				overwritingAggPresetAdjmt,
			})
		}
	}
	logAiInfo("ApplyPreset isOverwriting:", isOverwriting)

	return { aggregatedPresetId, isOverwriting, baseAdjustments }
}

export function includesAIMask(adjustments: Adjustments) {
	const localAdjustments = adjustments.localAdjustments ?? []
	for (const localAdjustment of localAdjustments) {
		for (const mask of localAdjustment.masks) {
			if (isObjectMask(mask)) return true
		}
	}
	return false
}

export async function checkAndRemoveNonExistingMasks(
	adjustments: Adjustments,
	keyAdjustments: Partial<Adjustments>,
) {
	const maskExistenceMap: Record<string, boolean> = {}

	const localAdjustments = adjustments.localAdjustments ?? []
	for (const localAdjustment of localAdjustments) {
		for (const mask of localAdjustment.masks) {
			if (!isObjectMask(mask)) maskExistenceMap[mask.type] = true
			else if (maskExistenceMap[mask.type] === undefined)
				maskExistenceMap[mask.type] = await AI.hasMaskDetected(mask)
		}
	}

	removeNonExistingMasks(adjustments, maskExistenceMap)
	removeNonExistingMasks(keyAdjustments, maskExistenceMap)
}

// This function will mutate the adjustments in place
function removeNonExistingMasks(
	adjustments: Partial<Adjustments>,
	maskExistenceMap: Record<string, boolean>,
) {
	let localAdjustments = adjustments.localAdjustments
	if (localAdjustments) {
		localAdjustments.forEach((maskGroup) => {
			maskGroup.masks = maskGroup.masks.filter((val) => maskExistenceMap[val.type])
		})
		localAdjustments = localAdjustments.filter((val) => val.masks.length > 0)
		adjustments.localAdjustments = localAdjustments.length > 0 ? localAdjustments : undefined
	}
}

export function getInputColorMatchFeatureWithFaceData(
	imageInfo: ImageInfoRecord | undefined,
): InputColorMatchFeaturesWithFaceData | null {
	if (!imageInfo || !imageInfo.features.colorMatch) return null

	return {
		...imageInfo.features.colorMatch,
		faceColors: imageInfo.faceColors ?? null,
		faceWeights: imageInfo.faceWeights ?? null,
	}
}

export function saveInputColorMatchFeatureToImageInfo(
	imageInfo: ImageInfoRecord,
	inputFeatures: InputColorMatchFeaturesWithFaceData,
) {
	imageInfo.features.colorMatch = {
		facePercent: inputFeatures.facePercent,
		avgFaceHsvl: inputFeatures.avgFaceHsvl,
		maxExposure: inputFeatures.maxExposure,
		colorPatches: inputFeatures.colorPatches,
		lumPercentiles: inputFeatures.lumPercentiles,
		isRefKeyPhoto: inputFeatures.isRefKeyPhoto,
	}
	imageInfo.faceColors = inputFeatures.faceColors
	imageInfo.faceWeights = inputFeatures.faceWeights
}

export function getAdvancedParams(adjustments: Partial<Adjustments>) {
	const result = { ...adjustments }
	const keysToRemove = [
		...lightKeys,
		"saturation",
		"hueOrange",
		"saturationOrange",
		"luminanceOrange",
	]
	keysToRemove.forEach((key) => {
		delete result[key]
	})
	return result
}

export function isMainParamsEqual(
	adjustments1: Partial<Adjustments>,
	adjustments2: Partial<Adjustments>,
) {
	const keysToRemove = [...lightKeys, "saturation", "temperature", "tint", "localAdjustments"]
	const mainParams1 = { ...adjustments1 }
	keysToRemove.forEach((key) => {
		delete mainParams1[key]
	})
	const mainParams2 = { ...adjustments2 }
	keysToRemove.forEach((key) => {
		delete mainParams2[key]
	})
	return equal(mainParams1, mainParams2)
}

function getEffectiveExposure(adjustments: Partial<Adjustments>) {
	const exposure = adjustments.exposure ?? 0
	const highlight = adjustments.highlights ?? 0
	const shadows = adjustments.shadows ?? 0
	const whites = adjustments.whites ?? 0

	return exposure + 0.25 * highlight + 0.4 * shadows + 0.2 * whites
}

/**
 * Compute a base white-balance for color match: auto-WB of the input photo,
 * then transfer the reference's WB "bias" (how much the photographer shifted
 * WB beyond auto) onto the input.
 *
 * The auto-WB here uses `getAutoWBParamsForColorMatch` which intentionally
 * weights the image-only illuminant more than the skin-weighted one, making
 * the bias comparison robust to skin-tone differences between input and ref.
 *
 * Three paths:
 * 1. **Same face type** (both have face or both don't): bias = ref's final WB
 *    minus ref's auto-WB. This isolates the photographer's intentional shift.
 * 2. **Different face type**: bias = ref's final WB minus ref's as-shot WB.
 *    Auto-WB estimates aren't comparable across face/no-face, so use raw delta.
 * 3. **No ref WB data** (JPEG refs without as-shot info): approximate bias by
 *    inverting each ref's auto-WB (what auto-WB would undo ≈ overall color tone
 *    preference), averaged across all refs.
 */
export function getBaseWB(options: {
	imageInfo: ImageInfoRecord
	allRefs: ImageInfoRecord[]
}): Partial<Adjustments> {
	const { imageInfo, allRefs } = options

	const asShotTemperature = imageInfo.features.meta.init_temp ?? 5000
	const asShotTint = imageInfo.features.meta.init_tint ?? 0

	let autoWbParams = imageInfo.features.autoWbParams
	if (!autoWbParams) {
		autoWbParams = getAutoWBParamsForColorMatch({
			illuminant: imageInfo.features.wb,
			asShotTemperature,
			asShotTint,
		})
		imageInfo.features.autoWbParams = autoWbParams
	}

	const result = { ...autoWbParams }

	const nearestRef = allRefs[0] as ImageInfoRecord

	const refWB = nearestRef.colorMatchReference?.whiteBalance
	if (refWB) {
		let refAutoWbParams = nearestRef.features.autoWbParams
		if (!refAutoWbParams) {
			refAutoWbParams = getAutoWBParamsForColorMatch({
				illuminant: nearestRef.features.wb,
				asShotTemperature: refWB.asShotTemperature,
				asShotTint: refWB.asShotTint,
			})
			nearestRef.features.autoWbParams = refAutoWbParams
		}
		const hasFace = !!imageInfo.features.wb.face
		const refHasFace = !!nearestRef.features.wb.face
		const sameType = hasFace === refHasFace
		const featureDist = getFeatureDistances(imageInfo, nearestRef)[4]

		if (sameType) {
			const biasTemp =
				(refWB.temperature - (refAutoWbParams.temperature ?? refWB.asShotTemperature)) /
				refWB.asShotTemperature
			const biasTint = refWB.tint - (refAutoWbParams.tint ?? refWB.asShotTint)
			const { tempBiasFinal, tintBiasFinal } = getClampedWBBias({
				featureDist,
				tempBias: biasTemp,
				tintBias: biasTint,
			})
			result.temperature = (result.temperature ?? 0) + tempBiasFinal * asShotTemperature
			result.tint = (result.tint ?? 0) + tintBiasFinal
			logColorMatch("getBaseWB same type", {
				asShotTemperature,
				asShotTint,
				autoWbParams,
				result,
				refWB,
				refAutoWbParams,
				biasTemp,
				biasTint,
				featureDist,
				tempBiasFinal,
				tintBiasFinal,
			})
		} else {
			const biasTemp = (refWB.temperature - refWB.asShotTemperature) / refWB.asShotTemperature
			const biasTint = refWB.tint - refWB.asShotTint
			const { tempBiasFinal, tintBiasFinal } = getClampedWBBias({
				featureDist,
				tempBias: biasTemp,
				tintBias: biasTint,
			})
			result.temperature = asShotTemperature + tempBiasFinal * asShotTemperature
			result.tint = asShotTint + tintBiasFinal
			logColorMatch("getBaseWB different type", {
				asShotTemperature,
				asShotTint,
				result,
				refWB,
				biasTemp,
				biasTint,
			})
		}
	} else {
		// Calculate average biasTemp and biasTint from all refs
		let totalBiasTemp = 0
		let totalBiasTint = 0
		let validRefs = 0

		for (const ref of allRefs) {
			const refInvAutoWbParams = getAutoWBParamsForColorMatch({
				illuminant: ref.features.wb,
				asShotTemperature: 5000,
				asShotTint: 0,
				imageWeight: 1,
			})
			const refBiasTemp = -((refInvAutoWbParams.temperature ?? 5000) - 5000) / 5000
			const refBiasTint = -(refInvAutoWbParams.tint ?? 0) - 0

			totalBiasTemp += refBiasTemp
			totalBiasTint += refBiasTint
			validRefs++
		}

		const biasTemp = validRefs > 0 ? totalBiasTemp / validRefs : 0
		const biasTint = validRefs > 0 ? totalBiasTint / validRefs : 0

		result.temperature = (result.temperature ?? 0) + biasTemp * asShotTemperature
		result.tint = (result.tint ?? 0) + biasTint
		logColorMatch("getBaseWB: use auto wb params + inv ref wb (averaged from all refs)", {
			asShotTemperature,
			asShotTint,
			result,
			autoWbParams,
			biasTemp,
			biasTint,
			totalRefs: validRefs,
		})
	}

	return result
}

/**
 * Compute a base exposure for color match, transferring the reference's
 * exposure intent onto the input while compensating for scene brightness
 * differences.
 *
 * Core idea: start from the ref's exposure, then add a delta that accounts
 * for how the input's auto-exposure differs from the ref's auto-exposure
 * (so a darker scene gets a proportionally larger boost).
 *
 * - **ref with parameters**: uses the ref's stored exposure directly.
 * - **JPEG ref without parameters**: no native exposure — synthesizes one by rendering the preset
 *   on a gray gradient and measuring the brightness shift to compensate. This is mainly to avoid
 *   the case that preset makes image too bright or too dark.
 *
 * Post-hoc safeguards:
 * - Clamps toward `maxExposure` (face-aware) when base is too bright and the
 *   input's lighting is far from the ref's, to avoid blowing out faces.
 * - Dampens negative exposure (×0.6) and floors it based on face luminance
 *   to avoid making faces too dark.
 * - Slightly reduces positive exposure (×0.8) to leave headroom for the
 *   neural color match LUT's own lighting adjustments.
 */
export function getBaseExposure(options: {
	imageInfo: ImageInfoRecord
	nearestRef: ImageInfoRecord
	inputFeatures: InputColorMatchFeaturesWithFaceData | null
	baseAdjustments: Partial<Adjustments>
}): number {
	const { imageInfo, nearestRef, inputFeatures, baseAdjustments } = options
	const refAdjustments = nearestRef?.aiAdjustmentsMap[refCurrentAdjustmentsId]

	let refExposure = refAdjustments?.exposure
	if (refExposure == undefined) {
		// ref is jpeg, has no exposure value in PN, render the preset on gray gradient and find compensation exposure
		const grayBandBefore = createGrayBand(256)
		const grayBandBeforeProPhoto = applyTransform(grayBandBefore, srgbToProphoto)
		const adjustments = { ...baseAdjustments }
		adjustments.exposure = 0
		adjustments.temperature = undefined
		adjustments.tint = undefined
		const grayWithPreset = renderCpu(grayBandBeforeProPhoto, adjustments, {
			inputColorSpace: ColorSpaceCpu.proPhoto,
			outColorSpace: ColorSpaceCpu.proPhoto,
			asShotTemp: imageInfo.features.meta.init_temp ?? 5000,
			asShotTint: imageInfo.features.meta.init_tint ?? 0,
			profileCurve: null,
			colorLookTable: null,
			profileLookTable: null,
			userLut: null,
		})
		// This is the exposure to bring the after-preset image to the same brightness as the before-preset image
		refExposure = getExposure(grayWithPreset, grayBandBeforeProPhoto, [], false)
	}
	const deltaExposure =
		((nearestRef?.features.light.avgLum ?? 0) - (imageInfo.features.light.avgLum ?? 0)) * 3

	const refAutoLightParams =
		nearestRef.features.autoLightParams ??
		getAutoLightParams(
			nearestRef.features.light,
			nearestRef.features.colorMatch?.avgFaceHsvl ?? null,
		)
	const autoLightParams =
		imageInfo.features.autoLightParams ??
		getAutoLightParams(
			imageInfo.features.light,
			imageInfo.features.colorMatch?.avgFaceHsvl ?? null,
		)
	const autoExposure = getEffectiveExposure(autoLightParams)
	let deltaExposureAuto = autoExposure - getEffectiveExposure(refAutoLightParams)
	if (!refAdjustments) {
		deltaExposureAuto = autoExposure * 0.4 + deltaExposureAuto * 0.4 + deltaExposure * 0.2
	}
	let baseExposure = refExposure + deltaExposureAuto

	const maxExposure =
		inputFeatures?.maxExposure ?? getMaxExposureWithFace(imageInfo.features.light)
	if (maxExposure != undefined && baseExposure > 0.8 && baseExposure > maxExposure) {
		const lightDist = getOverallLightDistance(imageInfo.features, nearestRef.features) - 0.05
		if (lightDist > 0) {
			const weight = clamp(1 - lightDist * 1.5, 0, 1)
			baseExposure = weight * baseExposure + (1 - weight) * maxExposure
		}
	}

	// try to avoid too dark base exposure
	if (baseExposure < 0) {
		baseExposure *= 0.6
		if (imageInfo.features.light.faceLum > 0.01) {
			const minExposure =
				Math.min(0.55 - imageInfo.features.light.faceLum, refExposure, 0.0) * 3
			baseExposure = Math.max(baseExposure, minExposure)
		}
	} else {
		baseExposure *= 0.8 // slightly reduce base exposure for more room by AI
	}

	logAiInfo("baseExposure", {
		refExposure,
		deltaExposure,
		deltaExposureAuto,
		baseExposure,
	})
	return baseExposure
}

/**
 * Computes colorStrength (0.6–1) from feature distance to the reference.
 * colorStrength controls how aggressively the neural LUT's color output is
 * applied — it's passed to transformImage() as the color channel blend factor.
 * When the input is far from the reference (distance > 0.28), we reduce it so
 * distant references don't impose a dramatic color shift.
 *
 * Used alongside lightStrength (see getLightStrength in parameters.ts) which
 * independently controls how much of the LUT's lighting effect is applied.
 */
export function getFeatureDistAndColorStrength(options: {
	imageInfo: ImageInfoRecord
	nearestRef: ImageInfoRecord
}): { colorStrength: number; featureDist: number } {
	const { imageInfo, nearestRef } = options
	let colorStrength = 1
	const nearestOverallDist = getTimeWeightDecreasedOverallDistToRef(imageInfo, nearestRef)
	if (nearestOverallDist > 0.28) {
		colorStrength = Math.min(
			clamp((0.48 - nearestOverallDist) / 0.4 + 0.5, 0.6, 1),
			colorStrength,
		)
	}
	return { colorStrength, featureDist: nearestOverallDist }
}

export function isParamsEmpty(adjustments: Partial<Adjustments>) {
	if (Object.keys(adjustments).length < 5) return true

	const defaultAdjustments = getDefaultAdjustments()
	let equalCount = 0
	for (const key in adjustments) {
		if (equal(adjustments[key], defaultAdjustments[key])) equalCount++
	}

	// if almost all keys are equal to default, it's empty
	if (equalCount >= Object.keys(adjustments).length - 4) return true // allow 4 keys to be different from default (unknown special cases?)
	return false
}

// Users (like Aaron) don't feel comfortable with AI use extreme lighting parameters (like 100, 95)
export function limitLightingRanges(
	adjustments: Partial<Adjustments>,
	nearestRef: ImageInfoRecord,
) {
	const refAdjustments = nearestRef?.aiAdjustmentsMap[refCurrentAdjustmentsId]
	const lightingLimits = getLightingLimits({ baseAdjustments: refAdjustments, limit: 0.75 })

	const result = { ...adjustments }
	result.shadows = clamp(
		result.shadows ?? 0,
		lightingLimits.minShadows,
		lightingLimits.maxShadows,
	)
	result.blacks = clamp(result.blacks ?? 0, lightingLimits.minBlacks, lightingLimits.maxBlacks)
	result.highlights = clamp(
		result.highlights ?? 0,
		lightingLimits.minHighlights,
		lightingLimits.maxHighlights,
	)
	result.whites = clamp(result.whites ?? 0, lightingLimits.minWhites, lightingLimits.maxWhites)
	result.contrast = clamp(
		result.contrast ?? 0,
		lightingLimits.minContrast,
		lightingLimits.maxContrast,
	)

	logAiInfo("limitLightingRanges", {
		adjustments,
		refAdjustments,
		lightingLimits,
	})
	return result
}
